"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Linkedin, Zap, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const experts = [{
  name: "Wesley Vuijk",
  position: "CO-FOUNDER & STRATEEG",
  bio: "Geboren ondernemer en succesvol day-trade mentor. Wesley weet wat het betekent om data te lezen, snel te schakelen en beslissingen te nemen die tellen. Hij liet het bewust achter zich omdat hij ergens anders nog sterker in gelooft. AI gecombineerd met menselijke verbinding als sleutel tot groei voor elke ondernemer. Strategisch, recht door zee en altijd eerlijk. Dat is wat Connected Online maakt tot wat het is.",
  specialty: "Marketing Strategie",
  linkedin: "https://www.linkedin.com/in/wesley-v-5358971a9",
  image: "https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/byjasminfotografie-78-scaled.jpg?v=i4uceo6b5tb"
}, {
  name: "Priscilla Vuijk",
  position: "CO-FOUNDER & CREATIVE LEAD",
  bio: "Ook wel het creatieve denklab. Priscilla denkt niet in hokjes. Nooit gedaan ook. Ze plukt een idee uit de lucht, draait het om, bekijkt het van alle kanten en komt terug met iets wat je zelf nooit had bedacht. Analytisch genoeg om de data te begrijpen, creatief genoeg om er een verhaal van te maken dat blijft hangen. Ze ziet waar anderen overheen kijken en vertaalt dat naar een merkidentiteit dat klopt. Dat is wat jouw merk nodig heeft.",
  specialty: "Content Design",
  linkedin: "https://www.linkedin.com/company/connected-online/posts/?feedView=all",
  image: "https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/byjasminfotografie-285-scaled.jpg?v=zqghgm96u2"
}];
export default function DeExperts() {
  return <section data-section-id="135344" id="de-mensen-achter-de-data" className="relative py-24 overflow-hidden !bg-[hsl(20,100%,98%)]">
      {}
      <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: `linear-gradient(to right, var(--color-foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--color-foreground) 1px, transparent 1px)`,
      backgroundSize: "60px 60px"
    }} aria-hidden="true" />

      {}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-accent-foreground/10 shadow-[0_0_15px_var(--color-accent)] -translate-x-1/2 pointer-events-none hidden lg:block" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center p-2 bg-accent/10 rounded-none mb-4 border border-accent/20">
            <Cpu className="size-5 text-accent-foreground mr-2" />
            <span className="font-mono text-sm font-semibold text-accent-foreground uppercase tracking-wider">
              Persoonlijke Aanpak
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-primary mb-6 uppercase font-default">Meet the team</h2>
          <p className="text-lg text-muted-foreground font-sans">Connected Online is ontstaan zoals de beste ideeën dat doen: vanuit nieuwsgierigheid naar AI, naar verhalen van ondernemers en hoe dat elkaar kan versterken. Tegelijk zagen we hoe snel het onpersoonlijk wordt, terwijl de beste AI-tools pas werken als de mens centraal staat.&nbsp;<span style={{
            color: 'rgb(0, 0, 0)',
            letterSpacing: '-0.32px'
          }}>Daarom bouwen wij aan een plek waar strategie, gevoel, AI en marketing samenkomen. Met verbinding als basis.</span></p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {experts.map((expert, i) => <HoverCard key={i} openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div data-index={i} className="group cursor-pointer h-full">
                  <Card className="h-full border-border/40 bg-card/80 backdrop-blur-sm hover:border-accent-foreground/30 hover:shadow-xl transition-all duration-500 rounded-none overflow-hidden relative">
                    {}
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <CardHeader className="flex flex-row items-start gap-5 pb-4 relative z-10">
                      <SafeHydrate><Avatar className="size-24 rounded-none border border-border shadow-sm group-hover:border-accent-foreground/40 transition-colors duration-300">
                        <AvatarImage src={expert.image} alt={expert.name} className="object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                        <AvatarFallback className="rounded-none bg-muted text-muted-foreground font-mono text-2xl">
                          {expert.name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div className="pt-2">
                        <CardTitle className="text-2xl font-bold tracking-tight font-default">
                          {expert.name}
                        </CardTitle>
                        <CardDescription className="text-accent-foreground font-mono text-xs mt-1.5 font-medium uppercase tracking-wider">
                          {expert.position}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="relative z-10">
                      <p className="text-muted-foreground leading-relaxed font-sans">
                        {expert.bio}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </HoverCardTrigger>
              <HoverCardContent align="start" sideOffset={16} className="w-80 bg-card backdrop-blur-xl border-accent-foreground/20 shadow-2xl rounded-none p-5">
                <div className="flex justify-between space-x-4">
                  <SafeHydrate><Avatar className="size-14 rounded-none border border-border">
                    <AvatarImage src={expert.image} className="grayscale" />
                    <AvatarFallback className="rounded-none font-mono">
                      {expert.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div className="space-y-2 flex-1">
                    <h4 className="text-sm font-bold leading-none font-default">
                      {expert.name}
                    </h4>
                    <p className="text-xs text-muted-foreground font-sans">
                      Connecteer voor strategische marketing inzichten en groei.
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-border/50 mt-3">
                      <Badge variant="outline" className="border-accent-foreground/30 text-accent-foreground font-mono text-[10px] uppercase tracking-wider rounded-none bg-accent/5">
                        <Zap className="size-3 mr-1.5" />
                        {expert.specialty}
                      </Badge>
                      <Button variant="ghost" size="icon" asChild className="size-8 rounded-none hover:bg-accent/10 hover:text-accent-foreground transition-colors">
                        <Link to={expert.linkedin} newTab>
                          <Linkedin className="size-4" />
                          <span className="sr-only">LinkedIn profiel van {expert.name}</span>
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>)}
        </div>
      </div>
    </section>;
}